import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, setAuthToken } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    await http.post("/auth/register", { email, password, name });
    return login(email, password);
  };

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch { /* ignore */ }
    setAuthToken(null);
    setUser(null);
  };

  const isPaid = !!user && (user.plan === "standard" || user.plan === "pro");
  const isPro = !!user && user.plan === "pro";

  const access = user ? (user.access || "trial") : "anonymous";
  const isSubscribed = access === "subscribed";
  const isTrial = access === "trial";
  const isExpired = access === "expired";
  const trialDaysLeft = user?.trial_days_left || 0;

  return (
    <AuthContext.Provider value={{
      user, setUser, loading, refresh, login, register, logout,
      isPaid, isPro, access, isSubscribed, isTrial, isExpired, trialDaysLeft,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
